import {NavLink} from "react-router-dom";
import {useContext, useEffect, useRef, useState} from "react";
import {useStripe} from "@stripe/react-stripe-js";
import {AxiosError} from "axios";

// Assets
import {IckonicLogo} from "../../../Assets/SVGs/Logos";
import ErrorMessage from "../../../Components/Text/ErrorMessage";
import IconSpinLoader from "../../../Assets/SVGs/Icons/Loading/SpinLoader";

// Components
import PlansSelector, {PriceOverrides} from "../../../Components/Layouts/Plans/PlansSelector";
import PaymentMethod from "../../../Components/Forms/Account/PaymentMethod";
import ButtonPillGradient from "../../../Components/Buttons/ButtonPillGradient";

// Context
import {GlobalInterfaceContext} from "../../../Context/GlobalInterfaceContext";
import {UserAuthenticationContext} from "../../../Context/UserAuthenticationContext";

// Models
import PlanDTO from "../../../Models/DTOs/Plan";
import {UserPaymentMethod} from "../../../Models/Interfaces/UserPaymentMethod";

// Api
import {RestartSubscription, CreateSubscription, ApplyUpgrade} from "../../../Api/Stripe";
import {GetSignupQuote, signupQuoteRequest, signupQuotesMatch} from "../../../Api/SignupQuote";
import {useSignupQuote} from "../../../Hooks/Stripe/useSignupQuote";
import SignupQuoteSummary from "../../../Components/Layouts/Plans/SignupQuoteSummary";

// Hooks
import useFetchUserSubscription from "../../../Hooks/User/useFetchUserSubscription";
import {useUpgradePreview} from "../../../Hooks/Stripe/useStripeUpgrade";

// Helpers
import {
    getStripeDiscount,
    getStripePriceId
} from "../../../Helpers/Currency/Stripe";
import {RoutePaths} from "../../../Constants/RoutePaths";
import IconArrowBack from "../../../Assets/SVGs/Icons/UI/ArrowBack";
import {getPaymentIntentClientAction} from "../../../Helpers/StripePaymentIntent";
import {createBillingAttemptId} from "../../../Helpers/BillingAttempt";
import {classifyChoosePlanSubscription} from "./choosePlanFlow";
import {getResolvedOffer, isPrepaidOffer, PromotionMode} from "../../../Helpers/BillingOffers";
import {clearPromotionCode, getPromotionCode} from "../../../Helpers/PromotionCode";

function ChoosePlan() {
    const
        stripe = useStripe(),
        {isDarkMode} = useContext(GlobalInterfaceContext),
        {userData, getValidAccessToken} = useContext(UserAuthenticationContext),
        {
            subscriptionData,
            subscriptionLoading,
            subscriptionError,
            subscriptionResolved,
        } = useFetchUserSubscription({getValidAccessToken, userId: userData?.id}),

        [plans, setPlans] = useState<PlanDTO[]>([] as PlanDTO[]),
        [selectedPlan, setSelectedPlan] = useState<PlanDTO | null>(null),
        [priceOverrides, setPriceOverrides] = useState<PriceOverrides[]>([] as PriceOverrides[]),
        [plansError, setPlansError] = useState<string>(""),
        [defaultPM, setDefaultPM] = useState<UserPaymentMethod | null>(null),
        [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false),
        [paymentMethodsError, setPaymentMethodsError] = useState(""),
        [showUpdateForm, setShowUpdateForm] = useState(false),
        [isSubmitting, setIsSubmitting] = useState(false),
        [errMsg, setErrMsg] = useState<string>(""),
        [billingAttemptId] = useState(createBillingAttemptId),
        submitInFlightRef = useRef(false),

        effectiveSubStatus = (
            subscriptionData?.Status?.trim() ||
            userData?.subscription?.status?.trim() ||
            ""
        ).toLowerCase(),
        billingInterval = subscriptionData?.BillingInterval?.toLowerCase(),
        isActive = effectiveSubStatus === "active",
        subscriptionAction = classifyChoosePlanSubscription(effectiveSubStatus, !!subscriptionData),
        requiresPaymentRecovery = subscriptionAction === "recover_payment",
        isPaused = subscriptionAction === "resume_paused",
        recoveryMessage = requiresPaymentRecovery
            ? "Your existing membership has a payment issue. Update your payment method from your account, then retry the outstanding payment."
            : isPaused
                ? "Your membership is paused. Resume it from your account instead of starting another subscription."
                : "",

        // 2. Routing booleans:
        // Incomplete is recovered server-side; incomplete_expired is terminal and
        // receives a new subscription. Both use the create endpoint contract.
        needsNewSubscription = subscriptionAction === "create" || subscriptionAction === "recover_incomplete",

        isCanceled = subscriptionAction === "restart",

        isMonthly = billingInterval === "month" && (subscriptionData?.BillingIntervalCount ?? 1) === 1,

        promotionMode: PromotionMode = !subscriptionResolved
            ? "none"
            : needsNewSubscription
                ? "acquisition"
                : isCanceled
                    ? "targeted_only"
                    : isActive && isMonthly
                        ? "annual_upgrade"
                        : "none",

        signupQuote = useSignupQuote({
            plan: selectedPlan,
            userId: userData?.id,
            getValidAccessToken,
            enabled: subscriptionResolved && needsNewSubscription && !!userData?.id,
        }),

        // PREVIEW (auto for active monthly)
        {
            priceMinor,
            error: previewError,
            priceId,
            planKey,
            targetPlanKey,
        } = useUpgradePreview({
            userId: userData?.id,
            subscriptionCadence: billingInterval,
            isActive: isActive && isMonthly,
            defaultPM,
            selectedPlan,
            auto: true,
            getValidAccessToken,
        }),

        submitDisabled = !selectedPlan || !defaultPM || isSubmitting ||
            subscriptionLoading || !subscriptionResolved || !!subscriptionError ||
            paymentMethodsLoading || !!paymentMethodsError || !!recoveryMessage ||
            (needsNewSubscription && (!signupQuote.quote || signupQuote.loading || !!signupQuote.error)),

        handleSubmit = async () => {

            // hard guard to help prevent double submit
            if (
                submitInFlightRef.current ||
                isSubmitting ||
                subscriptionLoading ||
                !subscriptionResolved ||
                !!subscriptionError ||
                paymentMethodsLoading ||
                !!paymentMethodsError
            ) return;

            if (!userData?.id || !selectedPlan || !defaultPM) return;

            if (recoveryMessage) {
                setErrMsg(recoveryMessage);
                return;
            }

            setErrMsg("");
            setPlansError("");
            submitInFlightRef.current = true;
            setIsSubmitting(true);

            try {
                // All billing mutations below (create / restart / upgrade) are
                // self-or-admin protected on the backend — fetch a valid token once.
                const accessToken = await getValidAccessToken();
                if (!accessToken) {
                    setErrMsg("Your session has expired. Please log in and try again.");
                    return;
                }

                // 1) NEW SUBSCRIPTION: user has account but no sub, OR they are a bounced 3D secure user
                if (needsNewSubscription) {
                    const planType = selectedPlan.period === 1 ? 1 : 0; // 0=monthly, 1=yearly
                    const priceId = getStripePriceId(selectedPlan);
                    const offer = getResolvedOffer(selectedPlan);

                    if (!offer && !selectedPlan.offerCatalogResolved && !priceId) {
                        setErrMsg("No Stripe price configured for this plan.");
                        return;
                    }

                    const expectedQuote = signupQuote.quote;
                    if (!expectedQuote || signupQuote.loading || signupQuote.error) {
                        setErrMsg("Please confirm your first payment before continuing.");
                        return;
                    }
                    const confirmedQuote = await GetSignupQuote(
                        signupQuoteRequest(selectedPlan, userData.id), new AbortController(), accessToken,
                    );
                    if (!signupQuote.isCurrent() || !signupQuotesMatch(expectedQuote, confirmedQuote)) {
                        signupQuote.retry();
                        setErrMsg("Your price changed. Please review your first payment before continuing.");
                        return;
                    }

                    const res = await CreateSubscription(
                        userData.email,
                        selectedPlan.planId,
                        userData.id,
                        planType,
                        accessToken,
                        new AbortController(),
                        {
                            offerKey: offer?.offer_key,
                            offerId: offer?.id,
                            offerVersion: offer?.version,
                            finalAmountMinor: offer?.final_amount_minor,
                            promotionCode: offer?.bucket === "targeted" ? getPromotionCode() ?? undefined : undefined,
                            priceId: offer || selectedPlan.offerCatalogResolved ? undefined : priceId,
                            couponId: offer || selectedPlan.offerCatalogResolved
                                ? undefined
                                : getStripeDiscount(selectedPlan),
                            paymentMethodId: defaultPM.id,
                            attemptId: billingAttemptId,
                            affiliateExpectedAmountMinor: expectedQuote.amount_due_minor,
                        }
                    );

                    if ((res as any).isAxiosError || (res as any).response) {
                        setErrMsg("Failed to create subscription. Please try again.");
                        return;
                    }

                    const {status, client_secret, payment_intent_status} = res as import("../../../Api/Stripe").CreateSubscriptionResponse;

                    // Handle 3D Secure
                    if (status === 'incomplete') {
                        const intentAction = getPaymentIntentClientAction(
                            payment_intent_status,
                            !!client_secret
                        );
                        if (intentAction === "return_to_account") {
                            window.location.assign(RoutePaths.Account);
                            return;
                        }
                        if (
                            intentAction !== "confirm_existing" &&
                            intentAction !== "confirm_with_payment_method"
                        ) {
                            setErrMsg("Payment authentication is pending. Return to your account and try again shortly.");
                            return;
                        }
                        if (!client_secret) {
                            setErrMsg("Payment authentication could not be started. Return to your account and try again.");
                            return;
                        }
                        if (!stripe) {
                            setErrMsg("Stripe not initialized");
                            return;
                        }

                        // Recover the existing intent as-is unless Stripe explicitly
                        // requires a PaymentMethod to be supplied again.
                        const {error, paymentIntent} = intentAction === "confirm_with_payment_method"
                            ? await stripe.confirmCardPayment(client_secret, {payment_method: defaultPM.id})
                            : await stripe.confirmCardPayment(client_secret);

                        if (error) {
                            setErrMsg(error.message || "Payment authentication failed");
                            return;
                        }

                        if (paymentIntent && paymentIntent.status === 'succeeded') {
                            clearPromotionCode();
                            window.location.href = "/account";
                            return;
                        }
                    }

                    // Immediate success
                    if (status === 'active' || status === 'trialing') {
                        clearPromotionCode();
                        window.location.href = "/account";
                        return;
                    }

                    setErrMsg("Payment incomplete. Please check your card.");
                    return;
                }

                // 2) RESTART: True returning user with a past valid subscription
                if (isCanceled) {
                    if (!stripe) {
                        setErrMsg("Stripe not initialized");
                        return;
                    }

                    const offer = getResolvedOffer(selectedPlan);
                    const response = await RestartSubscription(userData.id, accessToken, new AbortController(), {
                        planType: selectedPlan.period === 1 ? 1 : 0,
                        offerKey: offer?.offer_key,
                        offerId: offer?.id,
                        offerVersion: offer?.version,
                        finalAmountMinor: offer?.final_amount_minor,
                        promotionCode: offer?.bucket === "targeted" ? getPromotionCode() ?? undefined : undefined,
                        priceId: offer || selectedPlan.offerCatalogResolved
                            ? undefined
                            : getStripePriceId(selectedPlan),
                        paymentMethodId: defaultPM.id
                    });

                    // Check for API errors
                    if (response instanceof AxiosError || (response as any).error) {
                        setErrMsg("Failed to restart subscription.");
                        return;
                    }

                    const {
                        status,
                        client_secret,
                        payment_intent_status,
                    } = response as import("../../../Api/Stripe").RestartSubscriptionResponse;

                    // Handle 3D Secure
                    if (status === 'incomplete') {
                        const intentAction = getPaymentIntentClientAction(
                            payment_intent_status,
                            !!client_secret
                        );
                        if (intentAction === "return_to_account") {
                            window.location.assign(RoutePaths.Account);
                            return;
                        }
                        if (
                            intentAction !== "confirm_existing" &&
                            intentAction !== "confirm_with_payment_method"
                        ) {
                            setErrMsg("Payment authentication is pending. Return to your account and try again shortly.");
                            return;
                        }
                        if (!client_secret) {
                            setErrMsg("Payment authentication could not be started. Return to your account and try again.");
                            return;
                        }

                        const {error, paymentIntent} = intentAction === "confirm_with_payment_method"
                            ? await stripe.confirmCardPayment(client_secret, {payment_method: defaultPM.id})
                            : await stripe.confirmCardPayment(client_secret);

                        if (error) {
                            setErrMsg(error.message || "Payment authentication failed");
                            return;
                        }

                        if (paymentIntent && paymentIntent.status === 'succeeded') {
                            clearPromotionCode();
                            window.location.href = "/account";
                            return;
                        }
                    }

                    // Immediate success
                    if (status === 'active' || status === 'trialing') {
                        clearPromotionCode();
                        window.location.href = "/account";
                        return;
                    }

                    setErrMsg("Payment incomplete. Please check your card.");
                    return;
                }

                // 3) UPGRADE (active monthly → something else)
                if (isActive && isMonthly) {
                    const pId = priceId || getStripePriceId(selectedPlan);
                    const selectedOffer = getResolvedOffer(selectedPlan);
                    if (isPrepaidOffer(selectedOffer)) {
                        setErrMsg("This introductory offer is available for new or restarted memberships. Choose a regular annual plan to upgrade.");
                        return;
                    }
                    const upgradeSelection = {
                        targetPlanKey: targetPlanKey || selectedPlan.key,
                        offerKey: selectedOffer?.offer_key,
                        offerId: selectedOffer?.id,
                        offerVersion: selectedOffer?.version,
                        finalAmountMinor: selectedOffer?.final_amount_minor,
                        legacyPriceId: selectedOffer || selectedPlan.offerCatalogResolved ? undefined : pId,
                    };

                    if (!upgradeSelection.targetPlanKey) {
                        setErrMsg("No upgrade plan is configured.");
                        return;
                    }

                    if (!stripe) {
                        setErrMsg("Stripe not initialized");
                        return;
                    }

                    let res = await ApplyUpgrade(userData.id, upgradeSelection, accessToken, new AbortController());

                    if (res && res.requires_action && res.client_secret && res.invoice_id) {
                        const { error, paymentIntent } = await stripe.confirmCardPayment(res.client_secret);

                        if (error) {
                            setErrMsg(error.message || "Payment authentication failed");
                            return;
                        }

                        if (paymentIntent && paymentIntent.status === 'succeeded') {
                            const refreshedToken = await getValidAccessToken();
                            if (!refreshedToken) {
                                setErrMsg("Your session has expired. Please log in and try again.");
                                return;
                            }
                            res = await ApplyUpgrade(
                                userData.id,
                                upgradeSelection,
                                refreshedToken,
                                new AbortController(),
                                res.invoice_id
                            );
                        }
                    }

                    if (res?.applied) {
                        clearPromotionCode();
                        window.location.href = "/account";
                        return;
                    }

                    setErrMsg("Upgrade did not complete. Please try again.");
                    return;
                }

                setErrMsg("Unsupported state. Please contact support.");
            } catch {
                setErrMsg("Your price could not be confirmed. Please try again.");
            } finally {
                submitInFlightRef.current = false;
                setIsSubmitting(false);
            }
        };

    useEffect(() => {
        const overrides =
            plans?.filter(p => p.key === planKey)
                .map(p => ({ planKey: p.key, planPrice: priceMinor })) ?? [];
        setPriceOverrides(overrides as PriceOverrides[]);
    }, [plans, planKey, priceMinor]);


    // open PM form if backend demanded it
    if (previewError && !errMsg) setErrMsg(previewError);
    return (
        <div className="page page--choose-plan page--no-nav is-dark-mode">
            <div className="page__header">
                <NavLink to="/">{IckonicLogo()}</NavLink>
            </div>

            <div className="page__heading">
                <h1>Choose Your Access to Unfiltered Truth</h1>
            </div>

            <div className="page__inner">
                <PlansSelector
                    onSelectPlan={(plan) => setSelectedPlan(plan)}
                    hasError={plansError !== ""}
                    isDarkMode={isDarkMode}
                    errorMessage={plansError}
                    featuresStart={0}
                    featuresEnd={10}
                    subscriptionData={subscriptionData}
                    promotionMode={promotionMode}
                    passPlanDataToParent={(plans) => setPlans(plans)}
                    priceOverrides={priceOverrides}
                />

                {needsNewSubscription && selectedPlan && <SignupQuoteSummary {...signupQuote} />}

                {userData?.id && (
                    <PaymentMethod
                        userId={userData.id}
                        getValidAccessToken={getValidAccessToken}
                        showUpdateForm={showUpdateForm}
                        setShowUpdateForm={setShowUpdateForm}
                        presentDefaultPM={setDefaultPM}
                        presentPaymentMethodsState={(loading, error) => {
                            setPaymentMethodsLoading(loading);
                            setPaymentMethodsError(error);
                        }}
                    />
                )}

                {(errMsg || recoveryMessage || subscriptionError) && (
                    <ErrorMessage errorMessage={errMsg || recoveryMessage || subscriptionError}/>
                )}

                {selectedPlan && (
                    <p className="choose-plan__terms-notice">
                        By continuing, you agree to the{" "}
                        <NavLink to={RoutePaths.TermsOfUse}>Terms of Use</NavLink>, including the
                        automatic renewal, cancellation and refund terms.
                    </p>
                )}

                <ButtonPillGradient
                    link=""
                    className={`choose-plan__submit ${isSubmitting ? "is-loading" : "is-not-loading"}`}
                    color={!selectedPlan || !defaultPM || isSubmitting ? "#818181" : "white"}
                    background="linear-gradient(225deg, #4676E6 0%, #B760B4 100%)"
                    backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #B760B4 100%)"
                    text={
                        isSubmitting
                            ? ""
                            : getResolvedOffer(selectedPlan)?.cta_label?.trim() || (isCanceled
                                ? "Restart Membership"
                                : isActive && isMonthly
                                    ? "Upgrade Membership"
                                    : "Subscribe to Ickonic")
                    }
                    disabled={submitDisabled}
                    icon={(
                        isSubmitting ||
                        subscriptionLoading ||
                        !subscriptionResolved ||
                        paymentMethodsLoading
                    ) && IconSpinLoader({
                        className: "icon--spin-loader",
                        iconFill: "white"
                    })}
                    onClick={handleSubmit}
                />

                <NavLink to={RoutePaths.Account} className="choose-plan__back-to-account">
                    {IconArrowBack()}
                    Back to account
                </NavLink>
            </div>
        </div>
    );
}

export default ChoosePlan;
